import { BillingEntity, OrderPayableBy, OrderPayableTo } from "../enums";

export type OrderLikeForPaymentRouteDisplay = {
  payableBy?: string;
  payableTo?: string;
  billingDetails?: {
    billingEntity?: string;
    isSingleGaraazInvoiceRequired?: boolean;
  };
  customer?: { _id?: string };
  customerId?: string;
};

export function isInsuranceBillingOrder(
  order: OrderLikeForPaymentRouteDisplay | null | undefined,
): boolean {
  return (
    order?.billingDetails?.billingEntity === BillingEntity.INSURANCE_COMPANY
  );
}

export function isSingleGaraazInvoiceEnquiry(
  order: OrderLikeForPaymentRouteDisplay | null | undefined,
): boolean {
  return order?.billingDetails?.isSingleGaraazInvoiceRequired === true;
}

/** Parses Mongo ObjectId strings from mobile config (comma/whitespace/newline-separated). */
export function parseNbfcCustomerIdsFromConfig(
  value: string | undefined | null,
): Set<string> {
  if (value == null || typeof value !== "string") {
    return new Set();
  }
  const ids = value.match(/[a-fA-F0-9]{24}/g) ?? [];
  return new Set(ids.map((id) => id.toLowerCase()));
}

export function isNbfcCustomer(
  customerId: string | undefined,
  nbfcCustomerIds: Set<string>,
): boolean {
  if (!customerId) return false;
  return nbfcCustomerIds.has(customerId.toLowerCase());
}

export const INSURANCE_ENQUIRY_PAYABLE_ROUTE = {
  payableBy: OrderPayableBy.INSURANCE,
  payableTo: OrderPayableTo.GARAAZ,
} as const;

export const NBFC_ENQUIRY_PAYABLE_ROUTE = {
  payableBy: OrderPayableBy.NBFC,
  payableTo: OrderPayableTo.GARAAZ,
} as const;

function resolveEnquiryDefaultPayableRoute(
  order: OrderLikeForPaymentRouteDisplay,
  nbfcCustomerIds: Set<string>,
): { payableBy: string; payableTo: string } | null {
  if (!isSingleGaraazInvoiceEnquiry(order)) {
    return null;
  }

  if (isInsuranceBillingOrder(order)) {
    return INSURANCE_ENQUIRY_PAYABLE_ROUTE;
  }

  const customerId =
    order.customer?._id?.toString?.() ??
    order.customerId?.toString?.() ??
    order.customer?._id ??
    order.customerId;

  if (isNbfcCustomer(customerId, nbfcCustomerIds)) {
    return NBFC_ENQUIRY_PAYABLE_ROUTE;
  }

  return null;
}

/**
 * Enquiry: show only when Single Invoice is enabled.
 * Confirmed: hide when Single Invoice is on; show when off and payable fields exist.
 */
export function shouldShowPaymentRouteBadge(params: {
  isEnquiryOrder: boolean;
  isSingleGaraazInvoiceRequired?: boolean;
  payableBy?: string;
  payableTo?: string;
}): boolean {
  const {
    isEnquiryOrder,
    isSingleGaraazInvoiceRequired,
    payableBy,
    payableTo,
  } = params;

  if (isEnquiryOrder) {
    return isSingleGaraazInvoiceRequired === true;
  }

  if (isSingleGaraazInvoiceRequired === true) {
    return false;
  }

  return !!(payableBy || payableTo);
}

export function getPaymentRouteDisplayValues(
  order: OrderLikeForPaymentRouteDisplay,
  isEnquiryOrder: boolean,
  nbfcCustomerIds: Set<string> = new Set(),
): { payableBy?: string; payableTo?: string } {
  if (!isEnquiryOrder) {
    return {
      payableBy: order.payableBy,
      payableTo: order.payableTo,
    };
  }

  const defaultRoute = resolveEnquiryDefaultPayableRoute(order, nbfcCustomerIds);
  if (defaultRoute) {
    return {
      payableBy: order.payableBy ?? defaultRoute.payableBy,
      payableTo: order.payableTo ?? defaultRoute.payableTo,
    };
  }

  return {
    payableBy: order.payableBy,
    payableTo: order.payableTo,
  };
}
